import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { RadioButton } from 'primereact/radiobutton';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useMemo, useState } from 'react';
import BeeButton from '../../Atoms/BeeButton';
import BeeIcon from '../../Atoms/BeeIcon';
import BeeLoadingSpinner from '../../Atoms/BeeLoadingSpinner';
import { formatDate } from '../../../Helper/Util/DateHelper';
import './AlphaPhaseImportSelectDialog.scss';
import { EMPTY_FIELD_PLACEHOLDER } from '../../../Helper/Statics/Constants';
import BeeContentHeadline from '../../Atoms/BeeContentHeadline';
import BeeTextInput from '../../Atoms/BeeTextInput';
import BeeDateInput from '../../Atoms/BeeDateInput';
import {
  PhasePriceUserMappingDTO,
  readAllKeyValuesForPhaseAndOrgaFromInputInstance
} from '../../../Helper/ApiHelper/KeyValueNetworkHelper';
import { ErrorResponseType } from '../../../Helper/ApiHelper/LoginNetworkHelper';
import {
  CopyCatalogInfo,
  MinimizedEpCatalog
} from '../../../Helper/ApiHelper/LvEpNetworkHelper';

export type PhaseImportOption = {
  phaseName: string;
  phaseId: string;
  phaseValidFrom?: string;
  phaseValidTo?: string;
  catalogTitle?: string;
  catalogVersion?: string;
  catalogId?: string | null;
  catalogVersionId?: string | null;
  pricesValidTo?: string;
};

type AlphaPhaseImportSelectDialogProps = {
  visible: boolean;
  targetUserId: string | null;
  allEpCatalogs: CopyCatalogInfo[];
  onHide: () => void;
  onConfirm: (selected: PhaseImportOption) => void;
  expectedCatalogId?: string | null;
  expectedCatalogVersionId?: string | null;
};

export default function AlphaPhaseImportSelectDialog({
  visible,
  targetUserId,
  allEpCatalogs,
  onHide,
  onConfirm,
  expectedCatalogId,
  expectedCatalogVersionId
}: AlphaPhaseImportSelectDialogProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<PhaseImportOption[]>([]);
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const tooltipAppendTo =
    typeof document !== 'undefined' ? document.body : undefined;
  useEffect(() => {
    if (!visible || !targetUserId) return;

    setLoading(true);

    readAllKeyValuesForPhaseAndOrgaFromInputInstance(targetUserId)
      .then((data: PhasePriceUserMappingDTO) => {
        const options = data?.phases?.map((phase) => {
          const epCatalog = allEpCatalogs.find(
            (epCatalog) => epCatalog.phaseId === phase.id
          );

          return {
            phaseName: phase.title,
            phaseId: phase.id,
            phaseValidFrom: phase.start,
            phaseValidTo: phase.end,
            catalogTitle: epCatalog?.catalogName,
            catalogVersion: epCatalog?.versionName,
            catalogId: epCatalog?.catalogId,
            catalogVersionId: epCatalog?.versionId,
            pricesValidTo: data?.phaseValidMapping?.[phase.id]
          };
        });
        setOptions(options);
      })
      .catch((error: ErrorResponseType) => {
        console.log(error);
        //fixme
      })
      .finally(() => {
        setLoading(false);
      });
  }, [visible, targetUserId, allEpCatalogs]);

  const selectedOption = useMemo(() => {
    if (!selectedPhaseId) return null;
    return options.find((o) => o.phaseId === selectedPhaseId) ?? null;
  }, [options, selectedPhaseId]);

  function handleConfirm() {
    if (!selectedOption) return;
    onConfirm(selectedOption);
  }

  function makeTooltipId(text: string, tooltipKey?: string) {
    // Ensure we get a valid/stable DOM id for PrimeReact's `target` selector.
    return (
      'alpha-phase-import-tooltip-' +
      (tooltipKey
        ? tooltipKey.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_') + '-'
        : '') +
      text.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_')
    );
  }

  function renderVerificationIconOrWarning({
    isOk,
    iconClass,
    warningText,
    tooltipKey,
    prefix
  }: {
    isOk: boolean;
    iconClass: string;
    warningText: string;
    tooltipKey?: string;
    prefix?: string;
  }) {
    if (isOk) {
      return (
        <div className="alpha-phase-import-verify-valid-icon">
          <BeeIcon iconClass={iconClass} type={'primary'} size={'small'} />
        </div>
      );
    }

    const tooltipContent = warningText;
    const tooltipId = makeTooltipId(prefix ?? '', tooltipKey);

    return (
      <>
        <div id={tooltipId} className="alpha-phase-import-verify-warning-icon">
          <BeeIcon
            iconClass={'pi pi-exclamation-circle'}
            type={'secondary'}
            size={'small'}
          />
        </div>
        <Tooltip
          target={`#${tooltipId}`}
          content={tooltipContent}
          style={{ whiteSpace: 'pre-line' }}
          appendTo={tooltipAppendTo}
        />
      </>
    );
  }

  function getWarningsForOption(option: PhaseImportOption, now: Date) {
    const warnings: string[] = [];

    if (expectedCatalogId != null) {
      const isCatalogTitleMatched =
        (option.catalogId ?? '').trim() === expectedCatalogId.trim();
      if (!isCatalogTitleMatched) {
        warnings.push('EP-Katalog ist nicht passend zur Mutterphase.');
      }
    } else {
      warnings.push('EP-Katalog der Mutterphase fehlt.');
    }

    if (expectedCatalogVersionId != null) {
      const isCatalogVersionMatched =
        (option.catalogVersionId ?? '').trim() ===
        expectedCatalogVersionId.trim();
      if (!isCatalogVersionMatched) {
        warnings.push('EP-Katalog Version ist nicht passend zur Mutterphase.');
      }
    } else {
      warnings.push('EP-Katalog Version der Mutterphase fehlt.');
    }

    if (!option.pricesValidTo) {
      warnings.push('Preise bis fehlt.');
    } else {
      const pricesValidToDate = new Date(option.pricesValidTo);
      const isPricesExpired = pricesValidToDate.getTime() < now.getTime();
      if (isPricesExpired) {
        warnings.push(
          `Preise sind am ${formatDate(option.pricesValidTo)} abgelaufen.`
        );
      }
    }

    return warnings;
  }

  function renderPhaseCards() {
    if (!options || options.length === 0) {
      return (
        <div className="alpha-phase-import-empty">
          Keine Importdaten verfügbar
        </div>
      );
    }

    const now = new Date();

    return (
      <div className="alpha-phase-import-cards flex flex-column gap-2">
        {options.map((opt) => {
          const isSelected =
            !!selectedPhaseId && opt.phaseId === selectedPhaseId;
          const optionWarnings = getWarningsForOption(opt, now);
          const pricesValidToDate =
            opt.pricesValidTo != null ? new Date(opt.pricesValidTo) : null;
          const isPricesExpired =
            pricesValidToDate != null &&
            pricesValidToDate.getTime() < now.getTime();

          const isCatalogTitleMatched =
            expectedCatalogId != null &&
            (opt.catalogId ?? '').trim() === expectedCatalogId.trim();

          const isCatalogVersionMatched =
            expectedCatalogVersionId != null &&
            (opt.catalogVersionId ?? '').trim() ===
              expectedCatalogVersionId.trim();
          return (
            <Card
              key={opt.phaseId}
              className={
                'alpha-phase-import-card ' +
                (isSelected ? 'alpha-phase-import-card--selected' : '')
              }
              onClick={() => setSelectedPhaseId(opt.phaseId)}
            >
              <div className="flex align-items-start justify-content-between gap-3 w-full">
                <div className="flex align-items-start gap-3 w-full">
                  <div className="alpha-phase-import-radio pt-3">
                    <RadioButton
                      inputId={'alpha-phase-import-radio-' + opt.phaseId}
                      name={'alpha-phase-import-selection'}
                      value={opt.phaseId}
                      checked={isSelected}
                      onChange={() => setSelectedPhaseId(opt.phaseId)}
                    />
                  </div>

                  <div className="w-full">
                    <div className="alpha-phase-import-card__header">
                      {renderPhaseHeader(opt)}
                    </div>
                    <div className="alpha-phase-import-card__meta mt-2">
                      <div className="alpha-phase-import-meta-row">
                        <div className="flex align-items-center gap-2">
                          <BeeTextInput
                            readOnly
                            label={renderEpCatalogLabel(
                              isCatalogTitleMatched,
                              opt
                            )}
                            value={opt.catalogTitle}
                          />
                          <BeeTextInput
                            readOnly
                            label={renderCatalogVersionLabel(
                              isCatalogVersionMatched,
                              opt
                            )}
                            value={opt.catalogVersion}
                          />
                          <BeeDateInput
                            readOnly
                            label={renderPricesValidToLabel(
                              opt,
                              isPricesExpired
                            )}
                            value={
                              opt.pricesValidTo
                                ? new Date(opt.pricesValidTo)
                                : undefined
                            }
                            onChange={() => {}}
                          />
                        </div>
                      </div>
                    </div>

                    {optionWarnings.length > 0 ? (
                      <div className="alpha-phase-import-warning-block mt-3">
                        <div className="alpha-phase-import-warning-block__icon">
                          <BeeIcon
                            iconClass={'pi pi-exclamation-circle'}
                            type={'secondary'}
                            size={'small'}
                          />
                        </div>
                        <ul className="alpha-phase-import-warning-block__list mb-0">
                          {optionWarnings.map((w) => (
                            <li key={w} style={{ whiteSpace: 'pre-line' }}>
                              {w}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    );
  }

  function renderPricesValidToLabel(
    opt: PhaseImportOption,
    isPricesExpired: boolean
  ): React.ReactNode {
    return (
      <div>
        <span>Preise bis:</span>{' '}
        {opt.pricesValidTo ? (
          <span className="alpha-phase-import-meta-row__icon">
            {renderVerificationIconOrWarning({
              isOk: !isPricesExpired,
              iconClass: 'pi pi-check-circle',
              warningText: `Preise laufen am ${formatDate(
                opt.pricesValidTo as string
              )} ab.`,

              tooltipKey: opt.phaseId,
              prefix: 'PreiseBis'
            })}
          </span>
        ) : (
          <span className="alpha-phase-import-meta-row__icon">
            {(() => {
              const tooltipText = 'Preise bis fehlt.';
              const tooltipId = makeTooltipId(tooltipText, opt.phaseId);

              return (
                <>
                  <div
                    id={tooltipId}
                    className="alpha-phase-import-verify-warning-icon"
                  >
                    <BeeIcon
                      iconClass={'pi pi-exclamation-circle'}
                      type={'secondary'}
                      size={'small'}
                    />
                  </div>
                  <Tooltip
                    target={`#${tooltipId}`}
                    content={tooltipText}
                    appendTo={tooltipAppendTo}
                  />
                </>
              );
            })()}
          </span>
        )}{' '}
      </div>
    );
  }

  function renderCatalogVersionLabel(
    isCatalogVersionMatched: boolean,
    opt: PhaseImportOption
  ): React.ReactNode {
    return (
      <div>
        <span>EP-Katalog Version:</span>{' '}
        {expectedCatalogVersionId != null ? (
          <span className="alpha-phase-import-meta-row__icon">
            {renderVerificationIconOrWarning({
              isOk: isCatalogVersionMatched,
              iconClass: isCatalogVersionMatched
                ? 'pi pi-check-circle'
                : 'pi pi-exclamation-circle',
              warningText:
                'EP-Katalog Version ist nicht passend zur Mutterphase.',
              tooltipKey: opt.phaseId,
              prefix: 'EP-KatalogVersion'
            })}
          </span>
        ) : null}
      </div>
    );
  }

  function renderEpCatalogLabel(
    isCatalogTitleMatched: boolean,
    opt: PhaseImportOption
  ): React.ReactNode {
    return (
      <div>
        <span>EP-Katalog:</span>{' '}
        {expectedCatalogId != null ? (
          <span className="alpha-phase-import-meta-row__icon">
            {renderVerificationIconOrWarning({
              isOk: isCatalogTitleMatched,
              iconClass: 'pi pi-check-circle',
              warningText: 'EP-Katalog ist nicht passend zur Mutterphase.',
              tooltipKey: opt.phaseId,
              prefix: 'EP-Katalog-title'
            })}
          </span>
        ) : null}
        {expectedCatalogId == null ? (
          <span className="alpha-phase-import-meta-row__icon">
            {(() => {
              const tooltipText = 'EP-Katalog der Mutterphase fehlt.';
              const tooltipId = makeTooltipId(tooltipText, opt.phaseId);

              return (
                <>
                  <div
                    id={tooltipId}
                    className="alpha-phase-import-verify-warning-icon"
                  >
                    <BeeIcon
                      iconClass={'pi pi-exclamation-circle'}
                      type={'secondary'}
                      size={'small'}
                    />
                  </div>
                  <Tooltip
                    target={`#${tooltipId}`}
                    content={tooltipText}
                    appendTo={tooltipAppendTo}
                  />
                </>
              );
            })()}
          </span>
        ) : null}
      </div>
    );
  }

  function renderPhaseHeader(opt: PhaseImportOption) {
    return (
      <>
        <BeeContentHeadline headline={'h3'} label={opt.phaseName} />
        <div className="alpha-phase-import-card__header__date text-sm ">
          {opt.phaseValidFrom
            ? formatDate(opt.phaseValidFrom)
            : EMPTY_FIELD_PLACEHOLDER}{' '}
          - {opt.phaseValidTo ? formatDate(opt.phaseValidTo) : '-'}
        </div>
      </>
    );
  }

  function renderFooter() {
    return (
      <div className="flex justify-content-end gap-2">
        <BeeButton
          type={'secondary'}
          label={'Abbrechen'}
          disabled={loading}
          isLoading={false}
          onClick={() => onHide()}
        />
        <BeeButton
          type={'primary'}
          label={'Übernehmen'}
          disabled={!selectedOption || loading}
          isLoading={false}
          onClick={() => handleConfirm()}
        />
      </div>
    );
  }

  return (
    <Dialog
      header={'Phase für Import auswählen'}
      visible={visible}
      modal={true}
      className={
        'bee-dialog-large bee-dialog-primary alpha-phase-import-dialog'
      }
      blockScroll={true}
      footer={renderFooter()}
      onHide={() => onHide()}
    >
      {loading ? (
        <div className="flex justify-content-center py-4">
          <BeeLoadingSpinner strokeWidth={'3'} type={'secondary'} />
        </div>
      ) : (
        <div className="alpha-phase-import-dialog-body">
          {renderPhaseCards()}
        </div>
      )}
    </Dialog>
  );
}
